import { CLERK_ORG_ID_CLAIM } from 'mdc-constants';

/**
 * Where the active org currently in hand came from.
 *
 * `storage` is not a degraded answer — on a cold offline boot it is the only one there is, because
 * Clerk resolves against the network. It is distinguished from `session` so a caller that must not
 * act on a stale org (a destructive action, a permission check) can tell the difference, while the
 * many callers that just need to scope a query do not have to care.
 */
export type ActiveOrgSource = 'session' | 'storage' | 'none';

/**
 * The org the app is acting as, identified by Clerk's id alone.
 *
 * There is deliberately no `organizations.id` uuid here. The backend maps `clerk_org_id` to its own
 * row through the unique `organizations.clerk_org_id` column, and so do the sync streams, so the
 * client never needs the uuid. The token used to carry it as `org_id`, copied from Clerk org
 * metadata that was missing for any org whose signup was interrupted (MDC-4505).
 */
export type ActiveOrg = {
  /** Clerk's organization id, `org_2abc…`. */
  clerkOrgId: string | null;
  /** Clerk's role slug for this membership, e.g. `org:admin`. */
  role: string | null;
  /** Whether Clerk itself has resolved. False does not mean there is no org — check `source`. */
  isLoaded: boolean;
  source: ActiveOrgSource;
};

/** What survives a restart. Mirrors `ActiveOrg` minus the fields that only describe this render. */
export type StoredActiveOrg = {
  clerkOrgId: string;
  role: string | null;
};

/**
 * Persistent record of the org the app was last acting as.
 *
 * It cannot live in the PowerSync database for the same reason `SyncIdentityStore` cannot: an org
 * switch wipes every table including the local-only ones, so the record would be destroyed by the
 * very event it exists to survive. Each platform supplies its own key-value store — `localStorage`
 * on web, MMKV on native.
 */
export type ActiveOrgStore = {
  read: () => StoredActiveOrg | null;
  write: (value: StoredActiveOrg) => void;
  clear: () => void;
};

export const NO_ACTIVE_ORG: ActiveOrg = {
  clerkOrgId: null,
  role: null,
  isLoaded: false,
  source: 'none',
};

/**
 * The claims bag as Clerk hands it to us. Deliberately loose: `CustomJwtSessionClaims` is augmented
 * per app, and this package is consumed by apps that do not augment it at all.
 */
export type SessionClaimsLike = { [CLERK_ORG_ID_CLAIM]?: unknown } | null | undefined;

const asNonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

export const readClerkOrgIdClaim = (sessionClaims: SessionClaimsLike): string | null =>
  asNonEmptyString(sessionClaims?.[CLERK_ORG_ID_CLAIM]);

type ResolveActiveOrgOptions = {
  isLoaded: boolean;
  /** `useAuth().orgId` — Clerk's own id, present without any custom template. */
  clerkOrgId: string | null | undefined;
  /** `useAuth().orgRole`. */
  role: string | null | undefined;
  sessionClaims: SessionClaimsLike;
  /** What was on disk when this app started. */
  restored: StoredActiveOrg | null;
};

/**
 * Combines what Clerk knows with what was last written to disk.
 *
 * Pure, and safe to call on every render: the write-through that keeps the store current is the
 * caller's job (see `useActiveOrgValue`), because a side effect here would fire during render.
 *
 * The live session always wins when it has an org. The stored value is only consulted while Clerk
 * has not resolved — the cold offline boot this whole store exists for. Once Clerk has resolved to
 * no org, that answer stands: the record on disk may belong to an org the user has since left, or
 * to the previous user on this device, since neither sign-out path clears it.
 */
export const resolveActiveOrg = ({
  isLoaded,
  clerkOrgId,
  role,
  sessionClaims,
  restored,
}: ResolveActiveOrgOptions): ActiveOrg => {
  const liveClerkOrgId = asNonEmptyString(clerkOrgId) ?? readClerkOrgIdClaim(sessionClaims);

  if (liveClerkOrgId) {
    return {
      clerkOrgId: liveClerkOrgId,
      role: asNonEmptyString(role) ?? (restored?.clerkOrgId === liveClerkOrgId ? restored.role : null),
      isLoaded,
      source: 'session',
    };
  }

  if (!isLoaded && restored) {
    return { ...restored, isLoaded, source: 'storage' };
  }

  return { ...NO_ACTIVE_ORG, isLoaded };
};
